/**
 * E6000 savings calculator — model V1 coefficient tables.
 *
 * Pure data, no DOM. Consumed by `savings-calculator.js`.
 * Source: PRD "E6000 销售计算器功能设计 V1.0" default parameter set.
 *
 * Structure (see PRD §6 / §11):
 *   product          — fixed E6000 constants (`product_config`)
 *   countries        — per-country tariff, grid CO₂ intensity, household defaults and regions
 *                      with pv_yield (`country_tariff`, `household_default`, `region_factor.pv_yield`)
 *   ratioBands       — PV / load ratio bands used to index the two experience factors
 *   directUseFactor  — direct_use_factor per band (share of PV used directly without a battery)
 *   batteryUtilization — battery_utilization_factor per band × units (effective full cycles / year)
 *   postcodeToRegion — postcode prefix → region name resolver per country
 *
 * Update these values without touching the UI code. Bump `factorVersion` when tables change.
 */
(() => {
  const product = {
    modelVersion: 'v1',
    factorVersion: '2026-09-v1.1',
    nominalCapacityKwh: 6,
    usableRatio: 0.95,
    roundTripEfficiency: 0.85,
    maxUnits: 3,
    incrementalPaybackLimitYears: 10,
    // Fixed rated power per solar panel used to convert panel count → capacity (W)
    panelWatt: 500
  };

  const countries = {
    NL: {
      currency: 'EUR',
      // Grid CO₂ intensity, kg CO₂ per kWh imported (CBS/RVO 2023 integral emission factor)
      gridEmissionFactor: 0.33,
      importPrice: 0.3,
      exportPrice: 0.01,
      postcodeExample: '1012AB',
      // Typical annual household load by household group 1~2, 3~4, 5+ people (kWh / year)
      householdLoads: [2850, 4400, 5800],
      regions: [
        { name: 'Utrecht', pvYield: 1000 },
        { name: 'Noord-Holland', pvYield: 950 },
        { name: 'Zuid-Holland', pvYield: 1000 },
        { name: 'Groningen', pvYield: 930 },
        { name: 'Friesland', pvYield: 930 },
        { name: 'Drenthe', pvYield: 940 },
        { name: 'Overijssel', pvYield: 970 },
        { name: 'Flevoland', pvYield: 970 },
        { name: 'Gelderland', pvYield: 1000 },
        { name: 'Noord-Brabant', pvYield: 1030 },
        { name: 'Zeeland', pvYield: 1050 },
        { name: 'Limburg', pvYield: 1050 }
      ]
    },
    DE: {
      currency: 'EUR',
      // Grid CO₂ intensity, kg CO₂ per kWh imported (UBA 2023 Strommix)
      gridEmissionFactor: 0.38,
      importPrice: 0.4,
      exportPrice: 0.08,
      postcodeExample: '10115',
      householdLoads: [2600, 4250, 5700],
      regions: [
        { name: 'Nordrhein-Westfalen', pvYield: 980 },
        { name: 'Bayern', pvYield: 1150 },
        { name: 'Baden-Württemberg', pvYield: 1120 },
        { name: 'Niedersachsen', pvYield: 980 },
        { name: 'Berlin', pvYield: 1050 },
        { name: 'Brandenburg', pvYield: 1040 },
        { name: 'Bremen', pvYield: 960 },
        { name: 'Hamburg', pvYield: 950 },
        { name: 'Hessen', pvYield: 1050 },
        { name: 'Mecklenburg-Vorpommern', pvYield: 1030 },
        { name: 'Rheinland-Pfalz', pvYield: 1060 },
        { name: 'Saarland', pvYield: 1080 },
        { name: 'Sachsen', pvYield: 1060 },
        { name: 'Sachsen-Anhalt', pvYield: 1040 },
        { name: 'Schleswig-Holstein', pvYield: 950 },
        { name: 'Thüringen', pvYield: 1040 }
      ]
    },
    FR: {
      currency: 'EUR',
      // Grid CO₂ intensity, kg CO₂ per kWh imported (ADEME Base Empreinte consumption mix)
      gridEmissionFactor: 0.056,
      importPrice: 0.25,
      exportPrice: 0.04,
      postcodeExample: '75008',
      householdLoads: [2600, 4300, 6000],
      regions: [
        { name: 'Île-de-France', pvYield: 1100 },
        { name: 'Grand Est', pvYield: 1100 },
        { name: 'Nouvelle-Aquitaine', pvYield: 1300 },
        { name: 'Occitanie', pvYield: 1400 },
        { name: 'Provence-Alpes-Côte d’Azur', pvYield: 1500 },
        { name: 'Auvergne-Rhône-Alpes', pvYield: 1250 },
        { name: 'Bourgogne-Franche-Comté', pvYield: 1150 },
        { name: 'Bretagne', pvYield: 1050 },
        { name: 'Centre-Val de Loire', pvYield: 1150 },
        { name: 'Corse', pvYield: 1550 },
        { name: 'Hauts-de-France', pvYield: 1000 },
        { name: 'Normandie', pvYield: 1050 },
        { name: 'Pays de la Loire', pvYield: 1150 }
      ]
    },
    UK: {
      currency: 'GBP',
      // Grid CO₂ intensity, kg CO₂ per kWh imported (DESNZ 2024 UK grid electricity conversion factor)
      gridEmissionFactor: 0.207,
      importPrice: 0.27,
      exportPrice: 0.12,
      postcodeExample: 'SW1A 1AA',
      householdLoads: [2800, 4500, 6200],
      regions: [
        { name: 'London', pvYield: 1000 },
        { name: 'South East', pvYield: 1050 },
        { name: 'South West', pvYield: 1050 },
        { name: 'East of England', pvYield: 1000 },
        { name: 'East Midlands', pvYield: 950 },
        { name: 'West Midlands', pvYield: 950 },
        { name: 'North East', pvYield: 900 },
        { name: 'North West', pvYield: 900 },
        { name: 'Yorkshire and the Humber', pvYield: 900 },
        { name: 'Wales', pvYield: 950 },
        { name: 'Scotland', pvYield: 850 },
        { name: 'Northern Ireland', pvYield: 850 }
      ]
    }
  };

  // PV / load ratio bands: < 0.5, 0.5–0.8, 0.8–1.2, 1.2–1.6, > 1.6
  const ratioBands = [0.5, 0.8, 1.2, 1.6, Infinity];

  // direct_use_factor per band (same for every country in V1 defaults)
  const directUseFactor = [0.72, 0.56, 0.32, 0.31, 0.3];

  // battery_utilization_factor per band → [1 unit, 2 units, 3 units]
  const batteryUtilization = [
    [180, 110, 75],
    [250, 160, 110],
    [315, 195, 140],
    [325, 210, 150],
    [290, 195, 140]
  ];

  const FR_DEPARTMENTS = [
    ['Île-de-France', ['75', '77', '78', '91', '92', '93', '94', '95']],
    ['Grand Est', ['08', '10', '51', '52', '54', '55', '57', '67', '68', '88']],
    ['Nouvelle-Aquitaine', ['16', '17', '19', '23', '24', '33', '40', '47', '64', '79', '86', '87']],
    ['Occitanie', ['09', '11', '12', '30', '31', '32', '34', '46', '48', '65', '66', '81', '82']],
    ['Provence-Alpes-Côte d’Azur', ['04', '05', '06', '13', '83', '84']],
    ['Auvergne-Rhône-Alpes', ['01', '03', '07', '15', '26', '38', '42', '43', '63', '69', '73', '74']],
    ['Bourgogne-Franche-Comté', ['21', '25', '39', '58', '70', '71', '89', '90']],
    ['Bretagne', ['22', '29', '35', '56']],
    ['Centre-Val de Loire', ['18', '28', '36', '37', '41', '45']],
    ['Corse', ['20']],
    ['Hauts-de-France', ['02', '59', '60', '62', '80']],
    ['Normandie', ['14', '27', '50', '61', '76']],
    ['Pays de la Loire', ['44', '49', '53', '72', '85']]
  ];

  const UK_AREAS = [
    ['Northern Ireland', ['BT']],
    ['Scotland', ['AB', 'DD', 'DG', 'EH', 'FK', 'G', 'HS', 'IV', 'KA', 'KW', 'KY', 'ML', 'PA', 'PH', 'TD', 'ZE']],
    ['Wales', ['CF', 'LD', 'LL', 'NP', 'SA', 'SY']],
    ['London', ['E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC']],
    ['North East', ['DH', 'DL', 'NE', 'SR', 'TS']],
    ['North West', ['BB', 'BL', 'CA', 'CH', 'CW', 'FY', 'L', 'LA', 'M', 'OL', 'PR', 'SK', 'WA', 'WN']],
    ['Yorkshire and the Humber', ['BD', 'DN', 'HD', 'HG', 'HU', 'HX', 'LS', 'S', 'WF', 'YO']],
    ['West Midlands', ['B', 'CV', 'DY', 'HR', 'ST', 'TF', 'WR', 'WS', 'WV']],
    ['East Midlands', ['DE', 'LE', 'LN', 'NG', 'NN', 'PE']],
    ['East of England', ['AL', 'CB', 'CM', 'CO', 'IP', 'LU', 'MK', 'NR', 'SG', 'SS']],
    ['South West', ['BA', 'BH', 'BS', 'DT', 'EX', 'GL', 'PL', 'SN', 'SP', 'TA', 'TQ', 'TR']],
    ['South East', ['BN', 'BR', 'CT', 'DA', 'GU', 'HP', 'KT', 'ME', 'OX', 'PO', 'RG', 'RH', 'SL', 'SM', 'SO', 'TN', 'TW']]
  ];

  // Dutch 4-digit postcode ranges → province (approximate, by first 4 digits)
  const NL_RANGES = [
    [1000, 1299, 'Noord-Holland'],
    [1300, 1399, 'Flevoland'],
    [1400, 1999, 'Noord-Holland'],
    [2000, 3399, 'Zuid-Holland'],
    [3400, 3999, 'Utrecht'],
    [4000, 4199, 'Gelderland'],
    [4200, 4299, 'Zuid-Holland'],
    [4300, 4699, 'Zeeland'],
    [4700, 5799, 'Noord-Brabant'],
    [5800, 6499, 'Limburg'],
    [6500, 7399, 'Gelderland'],
    [7400, 7799, 'Overijssel'],
    [7800, 7999, 'Drenthe'],
    [8000, 8199, 'Overijssel'],
    [8200, 8399, 'Flevoland'],
    [8400, 9299, 'Friesland'],
    [9300, 9499, 'Drenthe'],
    [9500, 9999, 'Groningen']
  ];

  function resolveNL(code) {
    if (!/^\d{4}/.test(code)) return null;
    const n = Number(code.slice(0, 4));
    const match = NL_RANGES.find(([min, max]) => n >= min && n <= max);
    return match ? match[2] : null;
  }

  function resolveDE(code) {
    if (!/^\d{5}$/.test(code)) return null;
    const n = Number(code.slice(0, 2));
    if (n <= 9) return 'Sachsen';
    if (n <= 14) return 'Berlin';
    if (n <= 16 || n === 19) return 'Brandenburg';
    if (n <= 18) return 'Mecklenburg-Vorpommern';
    if (n <= 22) return 'Hamburg';
    if (n <= 25) return 'Schleswig-Holstein';
    if (n <= 31 || (n >= 37 && n <= 38)) return 'Niedersachsen';
    if (n <= 33 || (n >= 40 && n <= 53) || (n >= 58 && n <= 59)) return 'Nordrhein-Westfalen';
    if (n <= 36 || (n >= 60 && n <= 65)) return 'Hessen';
    if (n === 39) return 'Sachsen-Anhalt';
    if (n <= 69) return n === 66 ? 'Saarland' : 'Rheinland-Pfalz';
    if (n <= 79 || n === 88 || n === 89) return 'Baden-Württemberg';
    if (n <= 97) return 'Bayern';
    return 'Thüringen';
  }

  function resolveFR(code) {
    if (!/^\d{5}$/.test(code)) return null;
    const dept = code.slice(0, 2);
    const match = FR_DEPARTMENTS.find(([, depts]) => depts.includes(dept));
    return match ? match[0] : null;
  }

  function resolveUK(code) {
    const area = code.match(/^[A-Z]{1,2}/);
    if (!area) return null;
    const match = UK_AREAS.find(([, areas]) => areas.includes(area[0]));
    return match ? match[0] : null;
  }

  /**
   * Whether the postcode is long enough to attempt a match. Used to decide
   * between "still typing" and "no match found" feedback.
   */
  function isPostcodeComplete(country, rawPostcode) {
    const digits = rawPostcode.replace(/\D/g, '');
    switch (country) {
      case 'NL':
        return digits.length >= 4;
      case 'UK':
        return rawPostcode.trim().length >= 2;
      case 'DE':
      case 'FR':
        return digits.length >= 5;
      default:
        return false;
    }
  }

  function postcodeToRegion(country, rawPostcode) {
    const code = String(rawPostcode || '').toUpperCase().replace(/\s+/g, '');
    if (!code) return null;
    switch (country) {
      case 'NL':
        return resolveNL(code);
      case 'DE':
        return resolveDE(code);
      case 'FR':
        return resolveFR(code);
      case 'UK':
        return resolveUK(code);
      default:
        return null;
    }
  }

  window.SamduoSavingsCalculatorData = Object.freeze({
    product,
    countries,
    ratioBands,
    directUseFactor,
    batteryUtilization,
    postcodeToRegion,
    isPostcodeComplete
  });
})();
